import {
  Button,
  Badge,
  ButtonGroup,
  Checkbox,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  CommandEmpty,
} from '@e2n/ui';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectAllItem } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/Filter/SelectAllItem';

type Abschnitt = { value: string; name: string };

// eslint-disable-next-line max-lines-per-function
export function LehrjahrFilter({ view }: { view: View }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { store } = useRotationsplanFeatureContext();
  const [filter, setFilter] = useState('');
  const {
    ui: {
      kalender: {
        filter: { abschnitte: kalenderFilter },
      },
      uebersicht: {
        filter: { abschnitte: uebersichtFilter },
      },
    },
  } = store;

  const defaultSelected =
    view === 'planung' ? kalenderFilter : uebersichtFilter;
  const [selectedAbschnitte, setSelectedAnschnitte] =
    useState<Abschnitt[]>(defaultSelected);

  function onChangeSelectedAbschnitt(abschnitt: Abschnitt) {
    if (
      selectedAbschnitte
        .map((abschnitt) => abschnitt.value)
        .includes(abschnitt.value)
    ) {
      const newAbschnitte = selectedAbschnitte.filter(
        (beruf) => beruf.value !== abschnitt.value,
      );
      setSelectedAnschnitte(newAbschnitte);
      store.setKalenderGliederungFilter(newAbschnitte);
      if (view === 'planung') {
        store.setKalenderGliederungFilter(newAbschnitte);
      } else {
        store.setUebersichtGliederungFilter(newAbschnitte);
      }
    } else {
      setSelectedAnschnitte([...selectedAbschnitte, abschnitt]);
      if (view === 'planung') {
        store.setKalenderGliederungFilter([...selectedAbschnitte, abschnitt]);
      } else {
        store.setUebersichtGliederungFilter([...selectedAbschnitte, abschnitt]);
      }
    }
  }

  function deselectAll() {
    setSelectedAnschnitte([]);
    if (view === 'planung') {
      store.setKalenderGliederungFilter([]);
    } else {
      store.setUebersichtGliederungFilter([]);
    }
  }

  function selectAll() {
    setSelectedAnschnitte(store.data.abschnitte);
    if (view === 'planung') {
      store.setKalenderGliederungFilter(store.data.abschnitte);
    } else {
      store.setUebersichtGliederungFilter(store.data.abschnitte);
    }
  }

  function onChangeFilter(input: string) {
    setFilter(input);
  }

  const filteredAbschnitte = store.data.abschnitte.filter((abschnitt) =>
    abschnitt.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()),
  );

  return (
    <>
      <Popover>
        <PopoverTrigger className="hidden md:flex" asChild>
          <FilterTrigger
            selectedAbschnitte={selectedAbschnitte}
            deselectAll={deselectAll}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-full p-2 px-0 bg-white md:max-w-70">
          <Command>
            <CommandInput
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            />
            <CommandList>
              <div className="mx-2 flex items-center border-b border-neutrals-300 px-2 py-3.5 text-sm font-bold">
                <SelectAllItem
                  allCount={store.data.abschnitte.length}
                  selectedCount={selectedAbschnitte.length}
                  selectAll={selectAll}
                  deselectAll={deselectAll}
                />
              </div>
              <CommandEmpty className="text-neutrals-700 pt-4 pb-1">
                {t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}
              </CommandEmpty>
              {store.data.abschnitte.map((abschnitt) => (
                <CommandItem
                  key={abschnitt.value}
                  value={abschnitt.name}
                  onSelect={() =>
                    onChangeSelectedAbschnitt({
                      name: abschnitt.name,
                      value: abschnitt.value,
                    })
                  }>
                  <ListItem
                    selectedAbschnitte={selectedAbschnitte}
                    abschnitt={abschnitt}
                    onChangeSelectedAbschnitt={onChangeSelectedAbschnitt}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <FilterTrigger
            selectedAbschnitte={selectedAbschnitte}
            deselectAll={deselectAll}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t('domain.mitarbeiter.rotationsplan.filter.lehrjahr')}
          </div>
          <Input
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-4">
                <SelectAllItem
                  allCount={store.data.abschnitte.length}
                  selectedCount={selectedAbschnitte.length}
                  selectAll={selectAll}
                  deselectAll={deselectAll}
                />
                <div className="w-full bg-neutrals-200 h-px"></div>
              </div>
              {!filteredAbschnitte.length && (
                <div className="text-neutrals-700 py-1 text-center text-sm">
                  {t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}
                </div>
              )}
              {filteredAbschnitte.map((abschnitt, index) => (
                <div
                  className="flex flex-col gap-4"
                  key={abschnitt.value}
                  onClick={() => onChangeSelectedAbschnitt(abschnitt)}>
                  <ListItem
                    selectedAbschnitte={selectedAbschnitte}
                    abschnitt={abschnitt}
                    onChangeSelectedAbschnitt={onChangeSelectedAbschnitt}
                  />
                  {index < filteredAbschnitte.length - 1 && (
                    <div className="w-full h-px bg-neutrals-200"></div>
                  )}
                </div>
              ))}
              <Button onClick={() => setOpen(false)}>
                {t('actions.fertig')}
              </Button>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function FilterTrigger({
  selectedAbschnitte,
  deselectAll,
  ...props
}: {
  selectedAbschnitte: Abschnitt[];
  deselectAll: () => void;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  return (
    <ButtonGroup {...props}>
      <Button className="flex items-center gap-2" variant="outline-secondary">
        {t('domain.mitarbeiter.rotationsplan.filter.lehrjahr')}
        {selectedAbschnitte.length > 0 && (
          <Badge type="pill" variant="secondary">
            {selectedAbschnitte.length}
          </Badge>
        )}
      </Button>
      {selectedAbschnitte.length > 0 && (
        <Button
          variant="outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            deselectAll();
          }}>
          <FontAwesomeIcon icon={faXmarkLarge} />
        </Button>
      )}
    </ButtonGroup>
  );
}

export function ListItem({
  selectedAbschnitte,
  abschnitt,
  onChangeSelectedAbschnitt,
}: {
  selectedAbschnitte: Abschnitt[];
  abschnitt: Abschnitt;
  onChangeSelectedAbschnitt: (abschnitt: Abschnitt) => void;
}) {
  return (
    <div className="flex items-center justify-between w-full font-normal">
      <div className="text-sm truncate whitespace-nowrap">{abschnitt.name}</div>
      <Checkbox
        checked={selectedAbschnitte
          .map((abschnitt) => abschnitt.value)
          .includes(abschnitt.value)}
        onCheckedChange={() => onChangeSelectedAbschnitt(abschnitt)}
        className="disabled:opacity-100 disabled:cursor-auto"
      />
    </div>
  );
}
